import { FormEvent, useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { CircularProgress } from "../components/CircularProgress/CircularProgress";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

const getPasswordError = (message: string): string => {
  const eus004Match = message.match(/\[EUS-004\]/);
  if (eus004Match) {
    return "Пароль уже использовался в течение последнего года";
  }

  const eus005Match = message.match(/\[EUS-005\]/);
  if (eus005Match) {
    return "Требуется строчная буква";
  }

  const eus006Match = message.match(/\[EUS-006\]/);
  if (eus006Match) {
    return "Требуется прописная буква";
  }

  const eus007Match = message.match(/\[EUS-007\].*?\[(.*?)\]/);
  if (eus007Match) {
    return `Требуется спецсимвол (хотя бы один из списка): ${eus007Match[1] || "@ % $ # * & ^ ( ) { } [ ] - < > / | _"}`;
  }

  const eus008Match = message.match(/\[EUS-008\].*?\[(\d+)\]/);
  if (eus008Match) {
    return `Длина пароля должна быть меньше или равна ${eus008Match[1]} символам`;
  }

  const eus009Match = message.match(/\[EUS-009\].*?\[(\d+)\]/);
  if (eus009Match) {
    return `Пароль должен быть больше или равен ${eus009Match[1]} символам`;
  }

  const eus010Match = message.match(/\[EUS-010\]/);
  if (eus010Match) {
    return "Пароль не может быть пустым";
  }

  const eus011Match = message.match(/\[EUS-011\]/);
  if (eus011Match) {
    return "Превышено максимальное количество неудачных попыток входа";
  }

  const eus012Match = message.match(/\[EUS-012\]/);
  if (eus012Match) {
    return "Пароль истек. Сбросьте пароль.";
  }

  const eus013Match = message.match(/\[EUS-013\]/);
  if (eus013Match) {
    return "Ссылка для смены пароля истекла";
  }

  return message;
};

export const SetPassword = () => {
  const { id } = useParams<{ id: string }>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordsMismatch = useMemo(
    () => !!password && !!confirm && password !== confirm,
    [password, confirm]
  );

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (!id || !password || !confirm || password !== confirm) {
      return;
    }
    setError(null);
    setLoading(true);
    axios.post(`/auth/set-password/${id}`, { password })
      .then(() => {
        setSent(true);
      })
      .catch((err: unknown) => {
        setError(getPasswordError(getApiErrorMessage(err, "Ошибка при установке пароля")));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, password, confirm]);

  return (
    <div className="auth-container">
      <div className="auth-floating-wrap">
        <Link to="/" className="auth-home-link auth-home-link--floating" aria-label="На главную">
          <ArrowLeft size={14} />
          <span className="auth-home-link-label">На главную</span>
        </Link>
        <form className="auth-form" onSubmit={handleSubmit}>
          <h1 className="auth-title">I N A R I</h1>
          {sent ? (
            <>
              <div className="footer-links">
                <span className="auth-link" style={{ cursor: "default" }}>
                  Пароль успешно изменен.
                </span>
              </div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  window.location.href = "/login";
                }}
              >
                Войти в систему
              </button>
            </>
          ) : (
            <>
              <div className="form-group">
                <div className="input-password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`input${error || passwordsMismatch ? " input-error" : ""}`}
                    placeholder="Введите пароль"
                    value={password}
                    autoFocus
                    autoComplete="new-password"
                    onChange={(e) => {
                      setError(null);
                      setPassword(e.target.value.replace(/[а-яёА-ЯЁ]/g, ""));
                    }}
                  />
                  {password && (
                    <button
                      type="button"
                      className="input-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <div className="input-password-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`input${error || passwordsMismatch ? " input-error" : ""}`}
                    placeholder="Повторите пароль"
                    value={confirm}
                    autoComplete="new-password"
                    onChange={(e) => {
                      setError(null);
                      setConfirm(e.target.value.replace(/[а-яёА-ЯЁ]/g, ""));
                    }}
                  />
                  {confirm && (
                    <button
                      type="button"
                      className="input-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  )}
                </div>
              </div>

              {(passwordsMismatch || error) && (
                <div className="form-error">{passwordsMismatch ? "Пароли не совпадают" : error}</div>
              )}

              <div className="button-wrapper">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={!id || !password || !confirm || passwordsMismatch || loading}
                >
                  {loading ? <CircularProgress size="sm" /> : "Установить пароль"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};
